import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getUpcomingEvents, getPastEvents, registerForEvent, cancelEventRegistration, getUserEventRegistrations, getCurrentMembership } from '../services/api';

import EventPaymentForm from '../components/events/EventPaymentForm';
import Alert from '../components/common/Alert';
import sanitizeHtml from '../utils/sanitizeHtml';

const FORMAT_LABELS = {
  in_person: 'In-person',
  online: 'Online',
  hybrid: 'Hybrid',
};

function Events({ user }) {
  const navigate = useNavigate();
  const [view, setView] = useState('upcoming'); // 'upcoming' or 'past'
  const [events, setEvents] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [membership, setMembership] = useState(null);
  const [filter, setFilter] = useState('all'); // all | online | hybrid | in_person
  const [cancelling, setCancelling] = useState(null);

  // Determine if the current user should see event management shortcuts
  const canManageEvents = Boolean(
    user?.is_staff ||
    user?.is_superuser ||
    (Array.isArray(user?.role_permissions) && user.role_permissions.some((permission) => permission?.can_manage_events)) ||
    user?.permissions?.can_manage_events
  );

  useEffect(() => {
    loadEvents();
    if (user) {
      loadRegistrations();
      loadMembership();
    }
  }, [view, user]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const eventData = view === 'upcoming'
        ? await getUpcomingEvents()
        : await getPastEvents();
      
      setEvents(eventData.results || eventData);
    } catch (error) {
      console.error('Error loading events:', error);
      setError('Failed to load events. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const loadRegistrations = async () => {
    try {
      const registrationsData = await getUserEventRegistrations();
      const all = registrationsData.results || registrationsData;
      // /event-registrations/ returns every registration in the system for
      // staff/admins (so admin views can list all registrants), not just
      // the caller's own - filter to the current user here or an admin
      // would see other members' registrations as their own, including in
      // the cancel flow below.
      setRegistrations(all.filter(reg => reg.user === user?.id));
    } catch (error) {
      console.error('Error loading registrations:', error);
    }
  };

  const loadMembership = async () => {
    try {
      const m = await getCurrentMembership();
      setMembership(m);
    } catch (e) {
      // Non-fatal: user may not have membership
      setMembership(null);
    }
  };

  const isRegistered = (eventId) => {
    return registrations.some(reg => reg.event === eventId && reg.status === 'registered');
  };

  const handleRegisterClick = (event) => {
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent(`/events/${event.id}`)}`);
      return;
    }
    setSelectedEvent(event);
    setRegisterModalOpen(true);
    setError('');
  };

  const handleRegister = async () => {
    if (!selectedEvent) return;

    const pricePaid = getRegistrationPrice(selectedEvent);

    // If event requires payment, show payment form
    if (pricePaid > 0) {
      setRegisterModalOpen(false);
      setPaymentModalOpen(true);
      return;
    }

    // Handle free event registration
    setSubmitting(true);
    setError('');

    try {
      await registerForEvent({
        event: selectedEvent.id
      });

      setSuccess(`Registration successful for "${selectedEvent.name}"!`);
      setRegisterModalOpen(false);
      setSelectedEvent(null);

      // Refresh events and registrations
      await loadEvents();
      await loadRegistrations();
    } catch (error) {
      setError(error.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const onPaymentSuccess = async (result) => {
    setPaymentModalOpen(false);
    setSuccess(`Successfully registered for "${selectedEvent.name}"! Payment confirmed.`);
    setSelectedEvent(null);

    // Refresh events and registrations
    await loadEvents();
    await loadRegistrations();
  };

  const onPaymentError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleCancelRegistration = async (event) => {
    const registration = registrations.find(reg => reg.event === event.id && reg.status === 'registered');
    if (!registration) return;

    const pricePaid = Number(registration.price_paid || 0);
    const confirmMessage = pricePaid > 0
      ? `Are you sure you want to cancel your registration for "${event.name}"?\n\n$${pricePaid.toFixed(2)} will be added to your account as credit. If you'd like a cash refund instead, please contact support.`
      : `Are you sure you want to cancel your registration for "${event.name}"?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setCancelling(event.id);
    setError('');
    setSuccess('');

    try {
      const response = await cancelEventRegistration(registration.id);

      // Show success message with refund info
      if (response.refund) {
        setSuccess(`Successfully cancelled registration for "${event.name}". ${response.refund.message}`);
      } else {
        setSuccess(`Successfully cancelled registration for "${event.name}"`);
      }

      // Refresh events and registrations
      await loadEvents();
      await loadRegistrations();
    } catch (err) {
      setError(err.message || 'Failed to cancel registration');
    } finally {
      setCancelling(null);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);
  };

  const getCommunityPrice = (event) => {
    if (!event) return 0;
    const amount = Number(event.base_price);
    return Number.isNaN(amount) ? 0 : amount;
  };

  const getProPrice = (event) => {
    if (!event) return null;
    if (event.pro_member_price === null || event.pro_member_price === undefined) return null;
    const amount = Number(event.pro_member_price);
    return Number.isNaN(amount) ? null : amount;
  };

  const getEarlyBirdCommunityPrice = (event) => {
    if (!event || !event.is_early_bird_active) return null;
    if (event.early_bird_price === null || event.early_bird_price === undefined) return null;
    const amount = Number(event.early_bird_price);
    return Number.isNaN(amount) ? null : amount;
  };

  const getEarlyBirdProPrice = (event) => {
    if (!event || !event.is_early_bird_active) return null;
    if (event.early_bird_pro_price === null || event.early_bird_pro_price === undefined) return null;
    const amount = Number(event.early_bird_pro_price);
    return Number.isNaN(amount) ? null : amount;
  };

  const isProMember = Boolean(membership?.membership_type?.benefits?.access_type === 'pro');

  // How many of the account's registrants get Pro pricing per event: 0 for
  // Community, defaults to 1 for Pro tiers, higher if benefits overrides it
  // (e.g. Family Pro). Mirrors MembershipType.event_pro_price_cap.
  const proPriceCap = (() => {
    const benefits = membership?.membership_type?.benefits;
    if (!benefits || benefits.access_type !== 'pro') return 0;
    return benefits.pro_price_cap ?? 1;
  })();

  // This account's Nth active registration for this specific event
  // (primary + associates combined) - mirrors payment_views.py exactly.
  const familyPositionFor = (event) => {
    if (!event) return 1;
    return registrations.filter(r => r.event === event.id && r.status === 'registered').length + 1;
  };

  // Mirrors EventDetail.jsx's registrationPrice calculation exactly:
  // early bird (Pro or Community) when active, else regular Pro/Community,
  // with a 10% family discount for any position beyond the account's
  // Pro-price cap that isn't its very first registration for the event.
  const getRegistrationPrice = (event) => {
    if (!event) return 0;

    const communityPrice = getCommunityPrice(event);
    const proPrice = getProPrice(event);
    const earlyBirdCommunityPrice = getEarlyBirdCommunityPrice(event);
    const earlyBirdProPrice = getEarlyBirdProPrice(event);
    const isEarlyBirdActive = Boolean(event.is_early_bird_active);

    const position = familyPositionFor(event);
    const appliesProPrice = isProMember && position <= proPriceCap;

    let price;
    if (isEarlyBirdActive && appliesProPrice && earlyBirdProPrice !== null) {
      price = earlyBirdProPrice;
    } else if (isEarlyBirdActive && earlyBirdCommunityPrice !== null) {
      price = earlyBirdCommunityPrice;
    } else if (appliesProPrice && proPrice != null) {
      price = proPrice;
    } else {
      price = communityPrice;
    }

    if (!appliesProPrice && position > 1) {
      price = price * 0.9;
    }

    return price;
  };

  const formatCurrency = (amount) => (amount === 0 ? 'Free' : `$${amount.toFixed(2)}`);

  const visibleEvents = events.filter(e => {
    if (filter === 'online') return Boolean(e?.has_online_component);
    if (filter === 'in_person') return e?.event_format === 'in_person';
    if (filter === 'hybrid') return e?.event_format === 'hybrid';
    return true;
  });

  const selectedRegistrationPrice = getRegistrationPrice(selectedEvent);

  return (
    <div className="container mx-auto px-4 py-6">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Events</h1>
        <p className="text-gray-600 mt-2">
          Browse and register for upcoming events or view past events.
        </p>
      </header>
      
      <Alert type="error" message={error} onClose={() => setError('')} />
      <Alert type="success" message={success} onClose={() => setSuccess('')} />
      
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-gray-200 mb-6 pb-2">
        <div className="flex gap-4">
          <button
            className={`py-2 px-4 border-b-2 ${
              view === 'upcoming'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent hover:border-gray-300'
            }`}
            onClick={() => setView('upcoming')}
          >
            Upcoming Events
          </button>
          <button
            className={`py-2 px-4 border-b-2 ${
              view === 'past'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent hover:border-gray-300'
            }`}
            onClick={() => setView('past')}
          >
            Past Events
          </button>
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:ml-auto">
          {canManageEvents && (
            <div className="flex gap-2">
              <Link
                to="/admin/events"
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 border border-blue-200 rounded hover:bg-blue-50 transition-colors"
              >
                Manage Events
              </Link>
              <Link
                to="/admin/events/new"
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
              >
                Create Event
              </Link>
            </div>
          )}
          {view === 'upcoming' && (
            <div className={`flex items-center gap-3 ${canManageEvents ? '' : 'md:ml-auto'}`}>
              <label className="text-sm text-gray-600">Filter:</label>
              <select
                className="border border-gray-300 rounded px-2 py-1 text-sm"
                value={filter}
                onChange={e => setFilter(e.target.value)}
              >
                <option value="all">All</option>
              <option value="online">Online</option>
              <option value="hybrid">Hybrid</option>
              <option value="in_person">In-person</option>
            </select>
            </div>
          )}
        </div>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visibleEvents.length > 0 ? (
            visibleEvents.map(event => {
              const primaryImage = event?.images?.length ? event.images[0].image : null;
              const format = event?.event_format || 'in_person';
              const formatLabel = FORMAT_LABELS[format] || 'In-person';
              const formatBadgeClass =
                format === 'online'
                  ? 'bg-blue-100 text-blue-800'
                  : format === 'hybrid'
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-green-100 text-green-800';

              return (
                <div
                  key={event.id}
                  className="bg-white rounded-lg shadow overflow-hidden flex flex-col"
                >
                  {primaryImage && (
                    <a href={primaryImage} target="_blank" rel="noopener noreferrer">
                      <img
                        src={primaryImage}
                        alt={`${event.name} featured visual`}
                        className="h-40 w-full object-contain bg-gray-100"
                      />
                    </a>
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex justify-between items-start mb-4">
                      <h2 className="text-xl font-semibold text-gray-900">{event.name}</h2>
                      <span className={`${formatBadgeClass} text-xs px-2 py-1 rounded whitespace-nowrap`}>
                        {formatLabel}
                      </span>
                    </div>

                    {event.speaker && (
                      <div className="flex items-center gap-2 mb-3">
                        {event.speaker_picture && (
                          <img
                            src={event.speaker_picture}
                            alt={`${event.speaker} headshot`}
                            className="w-10 h-10 rounded-full object-cover border"
                          />
                        )}
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Speaker:</span> {event.speaker}
                        </p>
                      </div>
                    )}

                    <div className="mb-4 text-sm text-gray-600 space-y-1">
                      <p>
                        <span className="font-medium">Starts:</span>{' '}
                        {formatDateTime(event.start_date)}
                      </p>
                      <p>
                        <span className="font-medium">Ends:</span>{' '}
                        {formatDateTime(event.end_date)}
                      </p>
                      <p>
                        <span className="font-medium">Location:</span>{' '}
                        {format === 'online'
                          ? 'Online'
                          : event.location || 'TBD'}
                      </p>
                      {event.has_online_component && event.online_url && (
                        <p className="truncate">
                          <a
                            className="text-blue-600 hover:underline"
                            href={event.online_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Join link
                          </a>
                        </p>
                      )}
                    </div>

                    <div
                      className="rich-text-content text-gray-700 mb-4 line-clamp-3 overflow-hidden"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(event.description) }}
                    />

                    {event.capacity === 0 ? (
                      <p className="text-sm text-gray-600 mb-4">Unlimited capacity</p>
                    ) : (event.available_spots !== null && (
                      <p className="text-sm text-gray-600 mb-4">
                        {event.available_spots > 0
                          ? `${event.available_spots} spots available`
                          : 'Event is full'}
                      </p>
                    ))}

                    <div className="text-sm text-gray-700 space-y-1 mb-4">
                      <div>
                        <span className="font-medium">
                          {event.is_early_bird_active ? 'Early Bird Community:' : 'Community:'}
                        </span>{' '}
                        {formatCurrency(getEarlyBirdCommunityPrice(event) ?? getCommunityPrice(event))}
                        {getEarlyBirdCommunityPrice(event) !== null && getEarlyBirdCommunityPrice(event) !== getCommunityPrice(event) && (
                          <span className="text-xs text-gray-500 line-through ml-2">{formatCurrency(getCommunityPrice(event))}</span>
                        )}
                      </div>
                      {getProPrice(event) != null && (
                        <div>
                          <span className="font-medium">
                            {event.is_early_bird_active ? 'Early Bird Pro:' : 'Pro Member:'}
                          </span>{' '}
                          {formatCurrency(getEarlyBirdProPrice(event) ?? getProPrice(event))}
                          {getEarlyBirdProPrice(event) !== null && getEarlyBirdProPrice(event) !== getProPrice(event) && (
                            <span className="text-xs text-gray-500 line-through ml-2">{formatCurrency(getProPrice(event))}</span>
                          )}
                        </div>
                      )}
                      {getProPrice(event) == null && getCommunityPrice(event) === 0 && (
                        <div className="text-gray-600">Free event</div>
                      )}
                    </div>
                  </div>

                  <div className="mt-auto p-4 bg-gray-50 border-t border-gray-100">
                    {view === 'upcoming' ? (
                      isRegistered(event.id) ? (
                        <div>
                          <div className="text-green-600 font-medium text-center mb-3">
                            ✓ You are registered
                          </div>
                          <div className="flex gap-2">
                            <Link className="flex-1 text-center py-2 px-4 rounded border border-blue-600 text-blue-600 hover:bg-blue-50" to={`/events/${event.id}`}>
                              View Details
                            </Link>
                            {(() => {
                              const registration = registrations.find(reg => reg.event === event.id && reg.status === 'registered');
                              return registration?.can_cancel && (
                                <button
                                  onClick={() => handleCancelRegistration(event)}
                                  disabled={cancelling === event.id}
                                  className="flex-1 py-2 px-4 rounded border border-red-600 text-red-600 hover:bg-red-50 disabled:opacity-50"
                                >
                                  {cancelling === event.id ? 'Cancelling...' : 'Cancel'}
                                </button>
                              );
                            })()}
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Link className="w-1/2 text-center py-2 px-4 rounded border border-blue-600 text-blue-600 hover:bg-blue-50" to={`/events/${event.id}`}>
                            Details
                          </Link>
                        <button
                          onClick={() => handleRegisterClick(event)}
                          disabled={event.is_full}
                          className={`w-1/2 py-2 px-4 rounded ${
                            event.is_full
                              ? 'bg-gray-300 cursor-not-allowed'
                              : 'bg-blue-600 hover:bg-blue-700 text-white'
                          }`}
                          >
                            {event.is_full ? 'Full' : user ? 'Register' : 'Sign In to Register'}
                          </button>
                        </div>
                      )
                    ) : (
                      <div className="text-gray-600 text-center">Event Completed</div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-span-1 md:col-span-2 lg:col-span-3">
              <p className="text-gray-600 text-center py-8">
                No {view} events found.
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Registration Modal */}
      {registerModalOpen && selectedEvent && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Register for Event
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 mb-4">
                  Complete your registration for "{selectedEvent.name}"
                </p>
                
                <div className="mb-4 text-left">
                  <p className="text-gray-700 mb-2">
                    <span className="font-medium">Date:</span> {formatDateTime(selectedEvent.start_date)}
                  </p>
                  <p className="text-gray-700 mb-4">
                    <span className="font-medium">Price:</span>{' '}
                    {formatCurrency(selectedRegistrationPrice)}
                  </p>
                  {isProMember && getProPrice(selectedEvent) != null && (
                    <p className="text-xs text-gray-500 mb-3">
                      Pro pricing automatically applies to your membership.
                    </p>
                  )}
                  {selectedRegistrationPrice > 0 && (
                    <p className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded p-2 mb-2">
                      You will be prompted to enter payment details in the next step.
                    </p>
                  )}
                </div>
              </div>
              
              {error && (
                <div className="mx-7 mb-3 text-left">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
              
              <div className="items-center px-4 py-3 flex justify-end space-x-4">
                <button
                  onClick={() => setRegisterModalOpen(false)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRegister}
                  disabled={submitting || (selectedEvent.capacity !== 0 && selectedEvent.is_full)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400"
                >
                  {submitting
                    ? 'Processing...'
                    : selectedEvent.capacity !== 0 && selectedEvent.is_full
                      ? 'Event Full'
                      : selectedRegistrationPrice > 0
                        ? 'Continue to Payment'
                        : 'Complete Registration'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {paymentModalOpen && selectedEvent && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <EventPaymentForm
              event={selectedEvent}
              amount={selectedRegistrationPrice}
              onSuccess={onPaymentSuccess}
              onError={onPaymentError}
              onClose={() => {
                setPaymentModalOpen(false);
                setSelectedEvent(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Events;
